/**
 * AuditRuleCardContainer
 *
 * Thin container that wires the presentational AuditRuleCard with Redux state,
 * action dispatches, and the section revert mechanism.
 *
 * Needed because hooks can't be called inside loops — each card needs its own
 * hook instances (useAppSelector, useSectionRevert, etc.).
 */

import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { selectAffectedSectionIds, selectInterrupt, selectInterruptType, useSectionRevert } from '@features/note-ai-edit';
import { MultiStepWizard } from '@features/note-ai-edit/components/MultiStepWizard';
import { QuickReplyArea } from '@features/note-ai-edit/components/QuickReplyArea';
import { useGetNoteQuery } from '@features/notes';
import { useCreateTaskFromRuleMutation } from '@features/rule-build/store';
import { rulesApi, useDisableRuleMutation } from '@features/rules';
import { useLazyGetRuleQuery } from '@features/rules/store/api';
import { useCan } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks/core';
import { useModal } from '@store/hooks/useModal';

import {
  failCardUpdate,
  resetCardState,
  setCardCompleted,
  setCardIgnored,
  startCardUpdate,
} from '../../store/auditUiSlice';
import {
  makeSelectCardAffectedSections,
  makeSelectCardChangeSummary,
  makeSelectCardCompletedVia,
  makeSelectCardGraduated,
  makeSelectCardStatus,
  selectAnyCardUpdating,
  selectPreFixSectionIds,
} from '../../store/selectors';
import type { RuleComplianceAssessment } from '../../types';
import { mapAssessmentToCardData } from '../../utils/mapCardData';
import type { InterruptHandlers } from '../cards';
import { AuditRuleCard } from '../cards';

interface AuditRuleCardContainerProps {
  assessment: RuleComplianceAssessment;
  noteId: string;
  encounterId?: string;
  onFixWithAI: (message: string) => void;
  onScrollToSection?: (sectionId: string) => void;
  expandMode?: 'inline' | 'modal';
  interruptHandlers?: InterruptHandlers;
}

export const AuditRuleCardContainer: React.FC<AuditRuleCardContainerProps> = ({
  assessment,
  noteId,
  encounterId,
  onFixWithAI,
  onScrollToSection,
  expandMode = 'inline',
  interruptHandlers,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { noteAudit } = useEventTracking();
  const { open: openModal } = useModal();
  const ruleId = assessment.rule_id;

  // Admin detection (ADR 0073 Phase 3 / GDN-1590 — parity with hasRole('practice_administrator'))
  const isPracticeAdmin = useCan('manage_members');

  // Admin mutations
  const [disableRule] = useDisableRuleMutation();
  const [createTaskFromRule] = useCreateTaskFromRuleMutation();
  const [fetchRule] = useLazyGetRuleQuery();

  // Selectors (memoized per ruleId)
  const selectStatus = useMemo(() => makeSelectCardStatus(ruleId), [ruleId]);
  const selectAffectedSections = useMemo(() => makeSelectCardAffectedSections(ruleId), [ruleId]);
  const selectCompletedVia = useMemo(() => makeSelectCardCompletedVia(ruleId), [ruleId]);
  const selectGraduated = useMemo(() => makeSelectCardGraduated(ruleId), [ruleId]);
  const selectChangeSummary = useMemo(() => makeSelectCardChangeSummary(ruleId), [ruleId]);

  const cardStatus = useAppSelector(selectStatus);
  const cardAffectedSections = useAppSelector(selectAffectedSections);
  const cardCompletedVia = useAppSelector(selectCompletedVia);
  const isGraduated = useAppSelector(selectGraduated);
  const changeSummary = useAppSelector(selectChangeSummary);
  const anyCardUpdating = useAppSelector(selectAnyCardUpdating);
  const currentAffectedSectionIds = useAppSelector(selectAffectedSectionIds);
  const preFixSectionIds = useAppSelector(selectPreFixSectionIds);
  const interrupt = useAppSelector(selectInterrupt);
  const interruptType = useAppSelector(selectInterruptType);

  // Section revert for undo
  const { revertSection, previousContent } = useSectionRevert({ noteId });

  // Note sections for sectionId → index lookup
  const { data: noteData } = useGetNoteQuery({ noteId }, { skip: !noteId });
  const sections = useMemo(
    () => (noteData?.content?.sections || []) as Array<{ section_id: string }>,
    [noteData?.content?.sections],
  );

  // Map API data to presentation shape (assessment-only, no rule definition enrichment)
  const cardData = useMemo(
    () => mapAssessmentToCardData(assessment),
    [assessment],
  );

  // --- Callbacks ---

  const handleApprove = useCallback(() => {
    dispatch(startCardUpdate({
      ruleId,
      preFixSectionIds: [...currentAffectedSectionIds],
      preFixSnapshotKeys: Object.keys(previousContent),
    }));
    noteAudit.ruleFixClicked({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: ruleId,
      compliance_status: assessment.compliant as 'no' | 'warning',
    });

    const ruleName = assessment.rule_title || assessment.rule_id;
    const reason = assessment.reason;
    const suggestion = assessment.suggestion;

    const parts = [
      `We need to update the note for compliance with this rule: ${ruleName}.`,
      reason ? `\n\nReason: ${reason}` : '',
      suggestion ? `\n\nSuggested fix: ${suggestion}` : '',
    ];
    onFixWithAI(parts.join(''));
  }, [dispatch, ruleId, currentAffectedSectionIds, previousContent, assessment, onFixWithAI, noteAudit, noteId, encounterId]);

  const handleIgnore = useCallback(() => {
    dispatch(setCardIgnored(ruleId));
    noteAudit.ruleDismissed({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: ruleId,
      compliance_status: assessment.compliant as 'no' | 'warning',
    });
  }, [dispatch, ruleId, noteAudit, noteId, encounterId, assessment.compliant]);

  const handleUndo = useCallback(async () => {
    noteAudit.ruleUndone({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: ruleId,
      previous_status: cardStatus as 'completed' | 'ignored',
      completed_via: cardCompletedVia,
    });
    for (const sectionId of cardAffectedSections) {
      if (!previousContent[sectionId]) continue;
      const sectionIndex = sections.findIndex(s => s.section_id === sectionId);
      if (sectionIndex === -1) continue;
      await revertSection(sectionId, sectionIndex);
    }
    dispatch(resetCardState(ruleId));
  }, [cardAffectedSections, previousContent, sections, revertSection, dispatch, ruleId, noteAudit, noteId, encounterId, cardStatus, cardCompletedVia]);

  const handleMarkResolved = useCallback(() => {
    dispatch(setCardCompleted({ ruleId, affectedSectionIds: [], completedVia: 'manual' }));
    noteAudit.ruleMarkedResolved({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: ruleId,
      compliance_status: assessment.compliant as 'no' | 'warning',
    });
  }, [dispatch, ruleId, noteAudit, noteId, encounterId, assessment.compliant]);

  const handleSectionClick = useCallback(
    (sectionId: string) => {
      noteAudit.ruleJumpToSectionClicked({
        note_id: noteId,
        encounter_id: encounterId,
        rule_id: ruleId,
        section_id: sectionId,
      });
      onScrollToSection?.(sectionId);
    },
    [onScrollToSection, noteAudit, noteId, encounterId, ruleId],
  );

  // --- Admin callbacks ---

  const handleViewAnalytics = useCallback(() => {
    noteAudit.ruleAnalyticsClicked({
      note_id: noteId,
      rule_id: ruleId,
      source: 'audit_sidebar',
    });
    navigate(`/jotaudit/rules/${ruleId}/analytics`);
  }, [navigate, ruleId, noteAudit, noteId]);

  const handleEditRule = useCallback(async () => {
    noteAudit.ruleEditClicked({
      note_id: noteId,
      rule_id: ruleId,
      source: 'audit_sidebar',
    });
    try {
      const result = await createTaskFromRule({ ruleId }).unwrap();
      navigate(`/rule-build/${result.task_id}`);
    } catch {
      console.debug('[JotAudit] Failed to create task from rule', ruleId);
    }
  }, [createTaskFromRule, navigate, ruleId, noteAudit, noteId]);

  const handleDisableRule = useCallback(async () => {
    try {
      await disableRule({
        ruleId,
        providerRuleDisableRequest: { scope: 'org' },
      }).unwrap();
      dispatch(setCardIgnored(ruleId));
      dispatch(
        rulesApi.util.invalidateTags([
          { type: 'Rule', id: ruleId },
          { type: 'RuleList', id: 'LIST' },
          { type: 'RuleSearch', id: 'LIST' },
        ]),
      );
      noteAudit.ruleDisabled({
        note_id: noteId,
        rule_id: ruleId,
        source: 'audit_sidebar',
      });
    } catch {
      console.debug('[JotAudit] Failed to disable rule', ruleId);
    }
  }, [disableRule, dispatch, ruleId, noteAudit, noteId]);

  // --- Modal expand ---

  const handleExpandModal = useCallback(async () => {
    noteAudit.ruleClicked({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: ruleId,
      compliance_status: assessment.compliant as 'yes' | 'no' | 'warning',
      has_relevant_sections: Boolean(assessment.relevant_sections?.length),
    });
    const result = await fetchRule(ruleId);
    if (result.data) {
      openModal(MODAL_TYPES.RULE_DETAIL, {
        rule: result.data,
        isAdmin: isPracticeAdmin,
        assessment,
      });
    }
  }, [noteAudit, noteId, encounterId, ruleId, assessment, openModal, fetchRule, isPracticeAdmin]);

  // Disable approve when another card is already updating
  const canApprove = !anyCardUpdating;

  // Undo safety guard: disabled when graduated or when no snapshots remain
  const isUndoable = cardStatus === 'completed' || cardStatus === 'ignored';
  const canUndo =
    isUndoable &&
    !isGraduated &&
    (cardCompletedVia !== 'ai' ||
      cardAffectedSections.some(id => !!previousContent[id]));

  // --- Interrupt handling (Phase 4: inline fix) ---

  const handleCancelFix = useCallback(async () => {
    // Revert any sections this fix changed before the interrupt arrived
    const fixedSectionIds = currentAffectedSectionIds.filter(
      id => !preFixSectionIds.includes(id) && !!previousContent[id],
    );
    for (const sectionId of fixedSectionIds) {
      const sectionIndex = sections.findIndex(s => s.section_id === sectionId);
      if (sectionIndex !== -1) {
        await revertSection(sectionId, sectionIndex);
      }
    }

    interruptHandlers?.onDismiss();
    dispatch(failCardUpdate());
  }, [currentAffectedSectionIds, preFixSectionIds, previousContent, sections, revertSection, interruptHandlers, dispatch]);

  const interruptContent =
    cardStatus === 'interrupted' && interrupt?.isActive && interruptHandlers ? (
      interruptType === 'MULTI_STEP' ? (
        <MultiStepWizard
          onComplete={interruptHandlers.onMultiStepComplete}
          onDismiss={handleCancelFix}
        />
      ) : (
        <QuickReplyArea
          onContinue={interruptHandlers.onContinue}
          onConfirm={interruptHandlers.onConfirm}
          onMultiStepComplete={interruptHandlers.onMultiStepComplete}
          onExpressInterest={interruptHandlers.onExpressInterest}
          onDismiss={handleCancelFix}
        />
      )
    ) : undefined;

  return (
    <AuditRuleCard
      data={cardData}
      status={cardStatus}
      completedVia={cardCompletedVia}
      isAdmin={isPracticeAdmin}
      onApprove={handleApprove}
      disableApprove={!canApprove}
      onIgnore={handleIgnore}
      onUndo={canUndo ? handleUndo : undefined}
      onMarkResolved={handleMarkResolved}
      onSectionClick={onScrollToSection ? handleSectionClick : undefined}
      onViewAnalytics={isPracticeAdmin ? handleViewAnalytics : undefined}
      onEditRule={isPracticeAdmin ? handleEditRule : undefined}
      onDisableRule={isPracticeAdmin ? handleDisableRule : undefined}
      expandMode={expandMode}
      onExpandModal={expandMode === 'modal' ? handleExpandModal : undefined}
      onLearnMore={expandMode === 'modal' ? handleExpandModal : undefined}
      interruptContent={interruptContent}
      onCancelFix={cardStatus === 'interrupted' ? handleCancelFix : undefined}
      changeSummary={changeSummary}
      canUndo={canUndo}
    />
  );
};
